import { Card } from "collections/Cards";
import { format } from "date-fns";
import { LoadingIndicator, PolyIcon } from "elements";
import {
  DeviceWithConnectionsFragment,
  useUserByIdQuery
} from "generated/graphql";
import { useQueryParamString } from "polyrouter";
import React from "react";
import { useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import {
  getDesktopInfo,
  getMobileInfo
} from "routes/Manage/DeviceUsers/Shared/utils/deviceUserUtils";

type AppInformation = DeviceWithConnectionsFragment | null;

const formatLastDetected = (lastDetected?: string) =>
  lastDetected
    ? format(new Date(lastDetected), "M/d/yyyy")
    : handleNullFields(lastDetected);

const AppStatus: React.FC<{
  title: string;
  fetching: boolean;
  "data-testid": string;
}> = ({ title, fetching, "data-testid": testId }) => (
  <li data-testid={testId}>
    {fetching ? (
      <LoadingIndicator className="inline" />
    ) : (
      <>
        <PolyIcon className="controls" icon="success" title={title} />
        {title}
      </>
    )}
  </li>
);

const AppInformationCard: React.FC<{
  cardTitle: string;
  appTitle: string;
  appInfo: AppInformation;
  fetching: boolean;
  testId: string;
}> = ({ cardTitle, appTitle, appInfo, fetching, testId }) => {
  const { t } = useTranslation();
  const lastDetected = formatLastDetected(appInfo?.lastDetected);

  return (
    <>
      <h6>{cardTitle}</h6>
      <ul className="user-info-list d-flex flex-column">
        <li>
          <label>{t("DEVICE")}</label>
        </li>
        {fetching ? (
          <LoadingIndicator className="inline" />
        ) : (
          <>
            <li data-testid={`${testId}-os`}>
              {handleNullFields(appInfo?.hardwareProduct)}
            </li>
            <li data-testid={`${testId}-os-version`}>
              {handleNullFields(appInfo?.hardwareRevision)}
            </li>
          </>
        )}
      </ul>
      <ul className="user-info-list d-flex flex-column">
        <li>
          <label>{appTitle}</label>
        </li>
        {fetching ? (
          <LoadingIndicator className="inline" />
        ) : (
          <>
            <li>{t("LAST_DETECTED_DATE", { date: lastDetected })}</li>
            <li>
              {t("CLIENT_APP_VERSION", {
                version: handleNullFields(appInfo?.softwareVersion)
              })}
            </li>
          </>
        )}
      </ul>
    </>
  );
};

export const UserInformationCards: React.FC<{
  firmwareUpdateInfo: {
    count: number;
    loading: boolean;
  };
}> = ({ firmwareUpdateInfo }) => {
  const { t } = useTranslation();
  const [user_id] = useQueryParamString("user_id");
  const [tenantId] = useQueryParamString("tenantId");
  const [desktopId] = useQueryParamString("desktopId");
  const [mobileId] = useQueryParamString("mobileId");

  const { data: userData, fetching: userFetching } = useUserByIdQuery({
    variables: {
      id: user_id as string
    }
  });

  // look up desktop info, ensuring deviceId and tenantId match
  const lensDesktopInfo = getDesktopInfo(
    userData?.user.user?.connections,
    tenantId,
    desktopId
  );

  const statusDesktop = t("RUNNING_LENS_DESKTOP", {
    version: handleNullFields(lensDesktopInfo?.softwareVersion)
  });

  // look up mobile info, ensuring deviceId and tenantId match
  const lensMobileInfo = getMobileInfo(
    userData?.user.user?.connections,
    tenantId,
    mobileId
  );
  const statusMobile = t("RUNNING_LENS_MOBILE", {
    version: handleNullFields(lensMobileInfo?.softwareVersion)
  });

  const userInfoCards = [
    {
      key: "status",
      show: true,
      content: (
        <>
          <h6>{t("STATUS")}</h6>
          <ul className="user-info-list status d-flex flex-column space-around">
            {
              <AppStatus
                title={statusDesktop}
                fetching={userFetching}
                data-testid="lens-desktop-version"
              />
            }
            {lensMobileInfo && (
              <AppStatus
                title={statusMobile}
                fetching={userFetching}
                data-testid="lens-mobile-version"
              />
            )}
            <li data-testid="firmware-update-info">
              {firmwareUpdateInfo.loading ? (
                <LoadingIndicator className="inline" />
              ) : firmwareUpdateInfo.count > 0 ? (
                <>
                  <div className="count-label">{firmwareUpdateInfo.count}</div>
                  {t("DEVICE_FIRMWARE_UPDATE_AVAILABLE")}
                </>
              ) : (
                <>
                  <PolyIcon
                    className="controls green-fill"
                    icon="success"
                    title={t("ALL_DEVICES_UP_TO_DATE")}
                  />
                  {t("ALL_DEVICES_UP_TO_DATE")}
                </>
              )}
            </li>
          </ul>
        </>
      )
    },
    {
      key: "desktop",
      show: true,
      content: (
        <AppInformationCard
          cardTitle={t("DESKTOP")}
          appTitle={t("LENS_DESKTOP")}
          appInfo={lensDesktopInfo}
          fetching={userFetching}
          testId="desktop"
        />
      )
    },
    {
      key: "mobile",
      show: !!lensMobileInfo,
      content: (
        <AppInformationCard
          cardTitle={t("MOBILE")}
          appTitle={t("LENS_MOBILE")}
          appInfo={lensMobileInfo}
          fetching={userFetching}
          testId="mobile"
        />
      )
    }
  ];
  return (
    <div className="device-user-cards grid stretched">
      {userInfoCards
        .filter(card => card.show)
        .map(card => (
          <Card key={card.key} className="table-card col-4 p-1">
            {card.content}
          </Card>
        ))}
    </div>
  );
};
